// src/components/views/OrgChartView.jsx
import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  GitBranch,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Building2,
} from "lucide-react";
import LeadershipView from "./LeadershipView";
import HierarchyView from "./HierarchyView";
import DepartmentGroup from "../common/DepartmentGroup";
import { useOrgChartData } from "../../hooks/useOrgChartData";

export default function OrgChartView({ companyData, onNewSearch }) {
  const { rawLeadershipData, rawHierarchyData } = companyData || {};

  const [viewMode, setViewMode] = useState("leadership");
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptSearch, setDeptSearch] = useState("");
  const [leadershipDeptFilter, setLeadershipDeptFilter] =
    useState("All Departments");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const {
    leadershipData,
    categorizedHierarchyData,
    categorizedLeadershipData,
  } = useOrgChartData(rawLeadershipData, rawHierarchyData);

  useEffect(() => {
    if (categorizedHierarchyData) {
      const { leadership, core } = categorizedHierarchyData;
      const defaultDept =
        leadership.length > 0
          ? leadership[0]
          : core.length > 0
          ? core[0]
          : null;
      setSelectedDept(defaultDept);
    }
  }, [categorizedHierarchyData]);

  const filteredCategorizedData = useMemo(() => {
    if (!deptSearch)
      return {
        hierarchy: categorizedHierarchyData,
        leadership: categorizedLeadershipData,
      };
    const lowerSearch = deptSearch.toLowerCase();
    const filterDepts = (category) =>
      category.filter((d) => d.name.toLowerCase().includes(lowerSearch));
    return {
      hierarchy: {
        leadership: filterDepts(categorizedHierarchyData.leadership),
        core: filterDepts(categorizedHierarchyData.core),
        support: filterDepts(categorizedHierarchyData.support),
      },
      leadership: {
        leadership: filterDepts(categorizedLeadershipData.leadership),
        core: filterDepts(categorizedLeadershipData.core),
        support: filterDepts(categorizedLeadershipData.support),
      },
    };
  }, [categorizedHierarchyData, categorizedLeadershipData, deptSearch]);

  const filteredLeadershipData = useMemo(() => {
    if (leadershipDeptFilter === "All Departments") return leadershipData;
    return {
      ...leadershipData,
      departments: leadershipData.departments.filter(
        (dept) => dept.name === leadershipDeptFilter
      ),
    };
  }, [leadershipData, leadershipDeptFilter]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <>
      {/* <header className="app-header">
        <h1>
          {rawHierarchyData?.name || "Organizational Structure"} Organisational
          Structure
        </h1>
        <div className="view-toggle">
          <button
            className={viewMode === "leadership" ? "active" : ""}
            onClick={() => setViewMode("leadership")}
          >
            <Users size={16} /> Leadership View
          </button>
          <button
            className={`hierarchy-button ${
              viewMode === "hierarchy" ? "active" : ""
            }`}
            onClick={() => setViewMode("hierarchy")}
          >
            <GitBranch size={16} /> Hierarchy View{" "}
            <span className="beta-tag">BETA</span>
          </button>
        </div>
      </header> */}
      <main className="content-with-sidebar">
        <button
          onClick={toggleSidebar}
          className={`sidebar-toggle-handle ${
            isSidebarCollapsed ? "glow-animation" : ""
          }`}
          style={{ left: isSidebarCollapsed ? "0px" : "281px" }}
        >
          {isSidebarCollapsed ? (
            <ChevronRight size={18} />
          ) : (
            <ChevronLeft size={18} />
          )}
        </button>
        <div className={`sidebar-org ${isSidebarCollapsed ? "collapsed" : ""}`}>
          <div style={{ width: "260px" }}>
            <input
              type="text"
              placeholder="🔍 Search departments..."
              value={deptSearch}
              onChange={(e) => setDeptSearch(e.target.value)}
              className="search-input mb-3"
            />
            {viewMode === "hierarchy" ? (
              <>
                {filteredCategorizedData.hierarchy.leadership.length > 0 && (
                  <DepartmentGroup
                    departments={filteredCategorizedData.hierarchy.leadership}
                    selectedDeptName={selectedDept?.name}
                    onSelectDept={setSelectedDept}
                  />
                )}
                {filteredCategorizedData.hierarchy.core.length > 0 && (
                  <>
                    <hr className="sidebar-separator" />
                    <DepartmentGroup
                      title="Core Departments"
                      departments={filteredCategorizedData.hierarchy.core}
                      selectedDeptName={selectedDept?.name}
                      onSelectDept={setSelectedDept}
                    />
                  </>
                )}
                {filteredCategorizedData.hierarchy.support.length > 0 && (
                  <>
                    <hr className="sidebar-separator" />
                    <DepartmentGroup
                      title="Support Departments"
                      departments={filteredCategorizedData.hierarchy.support}
                      selectedDeptName={selectedDept?.name}
                      onSelectDept={setSelectedDept}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <div className="department-group">
                  <div className="dept-grid">
                    <motion.div
                      className={`dept-card ${
                        leadershipDeptFilter === "All Departments"
                          ? "active"
                          : ""
                      }`}
                      onClick={() => setLeadershipDeptFilter("All Departments")}
                    >
                      <div
                        className="dept-icon"
                        // style={{ backgroundColor: "#6c757d", color: "#606a74" }}
                      >
                        <LayoutGrid size={24} />
                      </div>
                      <div className="dept-name">All Departments</div>
                    </motion.div>
                  </div>
                </div>
                {filteredCategorizedData.leadership.leadership.length > 0 && (
                  <>
                    <hr className="sidebar-separator" />
                    <DepartmentGroup
                      departments={
                        filteredCategorizedData.leadership.leadership
                      }
                      selectedDeptName={leadershipDeptFilter}
                      onSelectDept={setLeadershipDeptFilter}
                      isLeadership={true}
                    />
                  </>
                )}
                {filteredCategorizedData.leadership.core.length > 0 && (
                  <>
                    <hr className="sidebar-separator" />
                    <DepartmentGroup
                      title="Core Functions"
                      departments={filteredCategorizedData.leadership.core}
                      selectedDeptName={leadershipDeptFilter}
                      onSelectDept={setLeadershipDeptFilter}
                      isLeadership={true}
                    />
                  </>
                )}
                {filteredCategorizedData.leadership.support.length > 0 && (
                  <>
                    <hr className="sidebar-separator" />
                    <DepartmentGroup
                      title="Support Departments"
                      departments={filteredCategorizedData.leadership.support}
                      selectedDeptName={leadershipDeptFilter}
                      onSelectDept={setLeadershipDeptFilter}
                      isLeadership={true}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div className="main-panel">
          {viewMode === "leadership" ? (
            <LeadershipView data={filteredLeadershipData} />
          ) : (
            <HierarchyView selectedDept={selectedDept} />
          )}
        </div>
      </main>
    </>
  );
}
